import { useMemo } from "react";
import useUserStore from "../stores/userStore";
import { localeToDir, resolveLocale } from "../utils/locale";
import { MESSAGES, PLATFORM_DEFAULT_LOCALE } from "./messages";

const FALLBACK_CHAIN_CACHE = new Map();

const getPathValue = (obj, path) => {
  if (!obj || typeof path !== "string") return undefined;
  return path.split(".").reduce((acc, segment) => {
    if (!acc || typeof acc !== "object") return undefined;
    return acc[segment];
  }, obj);
};

const interpolate = (template, values = {}) => {
  if (typeof template !== "string") return "";
  return template.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_match, token) => {
    const v = values[token];
    if (v === null || typeof v === "undefined") return "";
    return String(v);
  });
};

const localeCandidates = (locale) => {
  const normalized = resolveLocale(locale, PLATFORM_DEFAULT_LOCALE);
  if (FALLBACK_CHAIN_CACHE.has(normalized)) {
    return FALLBACK_CHAIN_CACHE.get(normalized);
  }

  const base = normalized.split("-")[0];
  const fallback = PLATFORM_DEFAULT_LOCALE;
  const fallbackBase = fallback.split("-")[0];
  const chain = Array.from(new Set([normalized, base, fallback, fallbackBase]));
  FALLBACK_CHAIN_CACHE.set(normalized, chain);
  return chain;
};

const getMessageTemplate = (locale, key) => {
  const chain = localeCandidates(locale);
  for (const candidate of chain) {
    const dict = MESSAGES[candidate];
    const template = getPathValue(dict, key);
    if (typeof template === "string") return template;
  }
  return null;
};

export const translateForLocale = (locale, key, values = {}, fallback = "") => {
  const template = getMessageTemplate(locale, key);
  if (!template) {
    return fallback || key;
  }
  return interpolate(template, values);
};

export const createTranslator = (locale) => {
  const resolvedLocale = resolveLocale(locale, PLATFORM_DEFAULT_LOCALE);

  const t = (key, values = {}, fallback = "") =>
    translateForLocale(resolvedLocale, key, values, fallback);

  const tp = (baseKey, count, values = {}, fallback = "") => {
    const numericCount = Number(count);
    const safeCount = Number.isFinite(numericCount) ? numericCount : 0;
    const rule = new Intl.PluralRules(resolvedLocale).select(Math.abs(safeCount));
    const withCount = { ...values, count: safeCount };
    const pluralKey = `${baseKey}_${rule}`;
    const otherKey = `${baseKey}_other`;
    const template = getMessageTemplate(resolvedLocale, pluralKey)
      || getMessageTemplate(resolvedLocale, otherKey);
    if (!template) return fallback || baseKey;
    return interpolate(template, withCount);
  };

  return { t, tp };
};

export const formatCurrency = (value, locale, currency = "EUR") => {
  const amount = Number(value);
  const safeAmount = Number.isFinite(amount) ? amount : 0;
  const resolvedLocale = resolveLocale(locale, PLATFORM_DEFAULT_LOCALE);
  try {
    return new Intl.NumberFormat(resolvedLocale, {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(safeAmount);
  } catch {
    return `${safeAmount.toFixed(2)} ${currency}`;
  }
};

export const formatNumber = (value, locale, options = {}) => {
  const amount = Number(value);
  const safeAmount = Number.isFinite(amount) ? amount : 0;
  const resolvedLocale = resolveLocale(locale, PLATFORM_DEFAULT_LOCALE);
  try {
    return new Intl.NumberFormat(resolvedLocale, options).format(safeAmount);
  } catch {
    return String(safeAmount);
  }
};

export const formatDateTime = (value, locale, options = {}) => {
  const date = value instanceof Date ? value : new Date(value);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const resolvedLocale = resolveLocale(locale, PLATFORM_DEFAULT_LOCALE);
  try {
    return new Intl.DateTimeFormat(resolvedLocale, options).format(date);
  } catch {
    return date.toISOString();
  }
};

export const useI18n = () => {
  const preferredLocale = useUserStore((state) =>
    state.preferredLocale || state.userData?.preferredLocale || PLATFORM_DEFAULT_LOCALE
  );

  const locale = resolveLocale(preferredLocale, PLATFORM_DEFAULT_LOCALE);

  return useMemo(() => {
    const { t, tp } = createTranslator(locale);
    const dir = localeToDir(locale);
    return {
      locale,
      dir,
      isRTL: dir === "rtl",
      t,
      tp,
    };
  }, [locale]);
};
